<template>
  <div id="concepts-listing">
    <header>
      <header-list />
    </header>
    <concepts-filters />
    <div class="concepts-listing" v-show="initialized">
      <div class="container">
        <div class="listing-header">
          <div class="item-count">{{ countLabel }}</div>
          <div class="filter filter_sort">
            <label>Sort By:</label>
            <select @change="onChangeOrderBy" :value="orderby">
              <option value="projectDate">Most Recent</option>
              <option value="title">Project Name</option>
              <option value="client">Client Name</option>
            </select>
          </div>
        </div>
        <ul class="thumbnails-grid">
          <li v-for="concept in concepts" :key="concept.id" v-if="concept.title && concept.concepts.length > 0" class="thumbnail">
            <div class="image">
              <a :href="getFullUrl(concept)" target="_blank">
                <img
                  v-if="concept.concepts && concept.concepts.length > 0"
                  class="lazyload"
                  data-sizes="auto"
                  :data-src="`${concept.concepts[0].url}?w=500&h=500&mode=crop&anchor=topcenter`"
                  :data-srcset="`${concept.concepts[0].url}?w=500&h=360&mode=crop&anchor=topcenter 1x, ${concept.concepts[0].url}?w=1000&h=1000&mode=crop&anchor=topcenter 2x`"
                />
              </a>
              <div
                v-if="concept.link && concept.link.url"
                class="thumbnail-overlay"
              >
                <a :href="concept.link.url" target="_blank" class="btn url-link"
                  ><span>{{ concept.link.url }}</span></a
                >
                <a class="btn share" :data-copy-on-click="getFullUrl(concept)"
                  ><span>Share</span></a
                >
              </div>
            </div>
            <div class="info">
              <a href="#">
                <span class="project-title">{{ concept.title }}</span>
                <span v-if="concept.client" class="client">{{
                  concept.client.title
                }}</span>
                <span
                  v-for="t in concept.type"
                  class="category"
                  >{{ t }}</span
                >
              </a>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="loading" class="loader" style="position: absolute; left:50%; transform: translateX(-50%);"></div>
  </div>
</template>

<script>
import conceptsService from "@/services/conceptsService";
import HeaderList from '@/components/HeaderList.vue';
import ConceptsFilters from '@/components/ConceptsFilters.vue';

const ORDERBY_STORAGE_KEY = "clue.concepts.orderby";
const DEFAULT_ORDERBY = "projectDate";

export default {
  name: "ConceptListView",

  components: {
    HeaderList,
    ConceptsFilters
  },

  data() {
    return {
      concepts: [],
      page: 1,
      pageSize: 24,
      totalRecords: 0,
      totalPages: 0,
      loading: false,
      initialized: false,
      unwatchFilters: null,
      unwatchSearch: null,
      unwatchOrderby: null,
    };
  },

  computed: {
    filters() {
      return this.$store.state.filters;
    },
    search() {
      return this.$store.state.search;
    },
    orderby() {
      return this.$store.state.orderby;
    },
    countLabel() {
      if (this.loading) {
        return "";
      } else if (this.totalRecords > 1) {
        return `${this.totalRecords} Concepts`;
      } else if (this.totalRecords > 0) {
        return `${this.totalRecords} Concept`;
      } else {
        if (!this.loading) {
          return "No data returned, please refine your filters";
        } else {
          return "";
        }
      }
    },
  },

  methods: {
    loadSavedOrderBy() {
      const savedOrderBy = localStorage.getItem(ORDERBY_STORAGE_KEY);
      return savedOrderBy || DEFAULT_ORDERBY;
    },
    async bindConcepts(page) {
      if (!page) page = 1;
      const result = await conceptsService.getConcepts(
        this.search,
        this.filters?.type,
        this.filters?.client,
        this.filters?.industry,
        this.filters?.year,
        this.orderby,
        page,
        this.pageSize
      );
      if (result.data.length > 0) {
        this.concepts.push(...result.data);
      }
      this.totalRecords = result.pageInfo.totalRecords;
      this.totalPages = result.pageInfo.totalPages;
      this.page = page;
    },
    async onLoadMore() {
      const page = this.page + 1;
      if (page > this.totalPages) {
        return;
      }
      this.loading = true;
      await this.bindConcepts(page);
      this.$nextTick(() => {
        $(".btn.share").copyOnClick({
          confirmClass: "copy-confirmation",
        });
      });
      this.loading = false;
    },
    onChangeOrderBy(e) {
      localStorage.setItem(ORDERBY_STORAGE_KEY, e.target.value);
      this.$store.dispatch("changeOrderBy", e.target.value);
    },
    getFullUrl(concept) {
      const absoluteURL = new URL(`/${concept.id}`, process.env.VUE_APP_DETAIL_BASEURL);
      return absoluteURL.href;
    },
    setupWatchers() {
      this.unwatchFilters = this.$watch(
        "filters",
        async () => {
          this.loading = true;
          this.concepts = [];
          await this.bindConcepts();
          this.loading = false;
        },
        { deep: true }
      );

      this.unwatchSearch = this.$watch("search", async () => {
        this.loading = true;
        this.concepts = [];
        await this.bindConcepts();
        this.loading = false;
      });

      this.unwatchOrderby = this.$watch("orderby", async () => {
        this.loading = true;
        this.concepts = [];
        await this.bindConcepts();
        this.loading = false;
      });
    }
  },

  created() {
    const savedOrderBy = this.loadSavedOrderBy();
    if (savedOrderBy !== this.$store.state.orderby) {
      this.$store.dispatch("changeOrderBy", savedOrderBy);
    }
  },

  async mounted() {
      this.loading = true;
      await this.bindConcepts();
        this.$nextTick(() => {
        $(".filter_sort select").SumoSelect();
        $(".btn.share").copyOnClick({
          confirmClass: "copy-confirmation",
        });
        this.loading = false;
        this.initialized = true;
        this.setupWatchers();

        // init scroll event
        window.onscroll = () => {
          let bottomOfWindow = document.documentElement.offsetHeight - document.documentElement.scrollTop - window.innerHeight < 400;
          if (bottomOfWindow && !this.loading) {
            // console.log('onscroll - loadmore', 
            //   document.documentElement.offsetHeight - document.documentElement.scrollTop - window.innerHeight,
            //   document.documentElement.scrollTop, window.innerHeight, document.documentElement.offsetHeight);
            this.onLoadMore();
          }
        }
      });
  },
};
</script>
